import React, { useEffect, useState } from "react";
import { LoadingButton } from "@mui/lab";
import {
  fortnoxGetStatus,
  fortnoxSendToClient,
  getLabelsByClientId,
  updateLabelsByClientId,
  fortnoxUpdateLabels,
  fortnoxSetImportAll,
  fortnoxUnauthorizeClient,
  fortnoxGetTokenCreateTime,
  fortnoxSetTokenCreateTime,
} from "API/swesum";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Radio } from "@mui/material";
import { usePopupNotificationContext } from "Context/NotificationPopup";
import CachedIcon from "@mui/icons-material/Cached";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import Settings from "@mui/icons-material/Settings";
import { useClient } from "Context/Client";
import {
  FORTNOX_STATUS,
  getFortnoxStatusColor,
  getFortnoxStatusTranslation,
} from "utils/options/FORTNOX_STATUS";
import FortnoxButton from "./FortnoxButton";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  borderRadius: "4px",
  boxShadow: 24,
  p: 6,
};

export default function Fortnox({ clientId }) {
  const { client, update, loading } = useClient();
  const { openSuccess, openError } = usePopupNotificationContext();
  const [loadingSave, setLoadingSave] = useState(false);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [status, setStatus] = useState(null);
  const [tokenCreateTime, setTokenCreateTime] = useState(null);
  const [labels, setLabels] = useState([]);

  // Etiketter
  const [importStatus, setImportStatus] = useState(
    client?.FortnoxImportAll ?? ""
  );

  const [fortnoxSetAsSent, setFortnoxSetAsSent] = useState(false);

  // Återraportering
  const [fortnoxReportBackPurchase, setFortnoxReportBackPurchase] =
    useState(false);
  const [fortnoxReportBackService, setFortnoxReportBackService] =
    useState(false);
  const [
    fortnoxReportBackPurchaseAccount,
    setFortnoxReportBackPurchaseAccount,
  ] = useState("");
  const [fortnoxReportBackServiceAccount, setFortnoxReportBackServiceAccount] =
    useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [modalSettingsOpen, setModalSettingsOpen] = useState(false);

  const handleModalSettingsClose = () => setModalSettingsOpen(false);

  const handleModalOpen = () => setModalOpen(true);
  const handleModalClose = () => {
    setModalOpen(false);
    setImportStatus(client?.FortnoxImportAll ?? "");
    setFortnoxReportBackPurchase(client?.FortnoxReportBackPurchase);
    setFortnoxReportBackService(client?.FortnoxReportBackService);
    setFortnoxReportBackPurchaseAccount(
      client?.FortnoxReportBackPurchaseAccount
    );
    setFortnoxReportBackServiceAccount(client?.FortnoxReportBackServiceAccount);
    setFortnoxSetAsSent(client?.FortnoxSetAsSent);
    asyncGetLabelsByClientId(clientId);
  };

  const asyncUpdateImportStatus = async () => {
    if (client) {
      setImportStatus(client?.FortnoxImportAll ?? "");

      setFortnoxReportBackPurchase(client?.FortnoxReportBackPurchase);
      setFortnoxReportBackService(client?.FortnoxReportBackService);
      setFortnoxReportBackPurchaseAccount(
        client?.FortnoxReportBackPurchaseAccount
      );
      setFortnoxReportBackServiceAccount(
        client?.FortnoxReportBackServiceAccount
      );
      setFortnoxSetAsSent(client?.FortnoxSetAsSent);
    }
  };

  const asyncHandleSendToClient = async () => {
    var res;

    if (status === FORTNOX_STATUS.VERIFIED) {
      res = await fortnoxUnauthorizeClient(clientId);

      if (res.status === 200) {
        openSuccess(res.data.Message);
      } else {
        openError(res.data.Message);
      }

      await asyncFetchStatus(clientId);
    } else {
      res = await fortnoxSendToClient(clientId);

      if (res.status === 200) {
        const url = res.data;
        window.location.href = url;
      }
    }
  };

  const asyncFetchStatus = async (clientId) => {
    const res = await fortnoxGetStatus(clientId);

    if (res.status === 200) {
      setStatus(res.data);
    }
  };

  const asyncFetchTokenCreateTime = async (clientId) => {
    const res = await fortnoxGetTokenCreateTime(clientId);

    if (res.status === 200) {
      setTokenCreateTime(res.data);
    }
  };

  const asyncGetLabelsByClientId = async (clientId) => {
    var res = await getLabelsByClientId(clientId);

    if (res.status === 200) {
      setLabels(res.data);
    }
  };

  const handleSelectChange = (value) => {
    setImportStatus(value);

    setLabels((prevLabels) =>
      prevLabels.map((label) => ({
        ...label,
        Excluded: value === false,
      }))
    );
  };

  const handleChange = (event, id) => {
    const newValue = event.target.value === "true";
    setLabels((prevLabels) =>
      prevLabels.map((label) =>
        label.Id === id ? { ...label, Excluded: newValue } : label
      )
    );
  };

  const updateTokenCreateTime = async () => {
    setLoadingSave(true);
    try {
      var res = await fortnoxSetTokenCreateTime(clientId, tokenCreateTime);

      if (res.status === 200) {
        openSuccess(`Uppdaterade startdatum till ${tokenCreateTime}`, 5000);
      } else {
        openError(`Något gick fel, ${res}`, 5000);
        console.error(res);
      }
    } finally {
      setLoadingSave(false);
    }
  };

  const updateLabelsAndReportBack = async () => {
    if (importStatus === "" || importStatus === null) {
      openError("Du har inte valt Importera/Exkludera alla.");
      return;
    }

    if (labels.some((label) => label.Excluded === null)) {
      openError("En etikett har inte något val.");
      return;
    }

    setLoadingSave(true);

    var resLabels = await updateLabelsByClientId(clientId, labels);
    var resSetImport = await fortnoxSetImportAll(clientId, importStatus);

    var resReportBack = await update({
      Id: client.Id,
      FortnoxReportBackPurchase: fortnoxReportBackPurchase,
      FortnoxReportBackService: fortnoxReportBackService,
      FortnoxReportBackPurchaseAccount: fortnoxReportBackPurchaseAccount,
      FortnoxReportBackServiceAccount: fortnoxReportBackServiceAccount,
      FortnoxSetAsSent: fortnoxSetAsSent,
    });

    if (
      resLabels.status === 200 &&
      resSetImport.status === 200 &&
      resReportBack.status === 201
    ) {
      openSuccess("Uppdaterat");

      client.FortnoxImportAll = importStatus;
      setImportStatus(importStatus);

      await asyncGetLabelsByClientId(clientId);
    } else {
      openError("Något gick fel, kunde inte uppdatera.");
    }

    setLoadingSave(false);
  };

  const asyncFortnoxUpdateLabels = async () => {
    setLoadingUpdate(true);

    var res = await fortnoxUpdateLabels(clientId);

    if (res.status === 200) {
      openSuccess("Etiketter uppdaterade från Fortnox.");
      await asyncGetLabelsByClientId(clientId);
    } else {
      openError(
        "Något gick fel, etiketter från Fortnox kunde inte uppdateras."
      );
    }

    setLoadingUpdate(false);
  };

  useEffect(() => {
    asyncFetchStatus(clientId);
    asyncGetLabelsByClientId(clientId);
    asyncFetchTokenCreateTime(clientId);
    asyncUpdateImportStatus();

    console.log(tokenCreateTime);
  }, [client]);

  return (
    <Paper
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        gap: "5px",
        padding: "32px",
        minHeight: "200px",
      }}
    >
      {client && (
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              paddingBottom: "1rem",
            }}
          >
            <Typography variant="subtitle2">Fortnox</Typography>
            <Chip
              label={getFortnoxStatusTranslation(status)}
              color={getFortnoxStatusColor(status)}
              sx={{ minWidth: "6rem" }}
            />
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem",
              }}
            >
              <FortnoxButton
                status={status}
                asyncHandleSendToClient={asyncHandleSendToClient}
                clientName={client.Name}
              />

              <Button
                variant="contained"
                onClick={handleModalOpen}
                sx={{ width: "100%" }}
                disabled={status !== FORTNOX_STATUS.VERIFIED}
                startIcon={<Settings />}
              >
                Inställningar
              </Button>
            </div>
          </div>
          <Modal
            open={modalSettingsOpen}
            onClose={handleModalSettingsClose}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
          >
            <Box sx={modalStyle}>
              <Typography
                variant="subtitle2"
                sx={{ marginBottom: "2rem", textAlign: "center" }}
              >
                Inställningar
              </Typography>
            </Box>
          </Modal>
          <Modal
            open={modalOpen}
            onClose={handleModalClose}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
          >
            <Box sx={modalStyle}>
              <Typography
                variant="subtitle2"
                sx={{ marginBottom: "2rem", textAlign: "center" }}
              >
                Fortnox - Inställningar
              </Typography>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div>
                  {status === FORTNOX_STATUS.VERIFIED ? (
                    <div style={{ display: "flex", gap: "1rem" }}>
                      <DatePicker
                        format="YYYY-MM-DD"
                        label="Startdatum för hämtning"
                        value={dayjs(tokenCreateTime)}
                        onChange={(val) =>
                          setTokenCreateTime(dayjs(val).format("YYYY-MM-DD"))
                        }
                        slotProps={{ textField: { size: "small" } }}
                        sx={{ width: "100%" }}
                      />
                      <LoadingButton
                        startIcon={<SaveIcon />}
                        sx={{ width: "100%", gap: "0.5rem" }}
                        variant="contained"
                        onClick={() => updateTokenCreateTime()}
                        loading={loadingSave}
                      >
                        Spara datum
                      </LoadingButton>
                    </div>
                  ) : null}
                  {status === FORTNOX_STATUS.VERIFIED ? (
                    <FormControl sx={{ width: "100%", marginTop: "2rem" }}>
                      <InputLabel>Importera alla</InputLabel>
                      <Select
                        sx={{ marginBottom: "1rem" }}
                        label="Importera alla"
                        value={importStatus}
                        onChange={(e) => handleSelectChange(e.target.value)}
                      >
                        <MenuItem value={true}>Ja</MenuItem>;
                        <MenuItem value={false}>Nej</MenuItem>;
                      </Select>
                      <div
                        style={{
                          display: "grid",
                          gridAutoRows: "1fr",
                          marginBottom: "2rem",
                          maxHeight: "30rem",
                          overflow: "auto",
                          borderBottom: "2px solid rgba(224, 224, 224, 1)",
                        }}
                      >
                        {labels.map((label, index) => {
                          return (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: "4rem",
                                borderBottom:
                                  index === labels.length - 1
                                    ? "none"
                                    : "2px solid #f6f3f4",
                                padding: "0.5rem",
                              }}
                            >
                              <FormLabel id={label.id}>
                                {label.Description}
                              </FormLabel>
                              <RadioGroup
                                id={label.Id}
                                name={label.Description}
                                sx={{
                                  display: "flex",
                                  flexDirection: "row",
                                }}
                                onChange={(e) => handleChange(e, label.Id)}
                                value={
                                  label.Excluded != null
                                    ? label.Excluded.toString()
                                    : ""
                                }
                              >
                                <FormControlLabel
                                  value="false"
                                  control={<Radio />}
                                  label="Importera"
                                />
                                <FormControlLabel
                                  value="true"
                                  control={<Radio />}
                                  label="Exkludera"
                                />
                              </RadioGroup>
                            </div>
                          );
                        })}
                      </div>
                    </FormControl>
                  ) : (
                    ""
                  )}
                </div>
                <div>
                  <Typography variant="subtitle2">Återrapportering</Typography>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      marginBottom: "1rem",
                      gap: "1rem",
                      borderBottom: "2px solid rgba(224, 224, 224, 1)",
                    }}
                  >
                    <div style={{ display: "flex" }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            disabled={loading}
                            checked={fortnoxReportBackPurchase}
                            onChange={(e) =>
                              setFortnoxReportBackPurchase(e.target.checked)
                            }
                          />
                        }
                        label="Köp"
                        sx={{ minWidth: "8rem" }}
                      />
                      <TextField
                        name="Köpkonto"
                        label="Köpkonto"
                        value={fortnoxReportBackPurchaseAccount}
                        onChange={(e) =>
                          setFortnoxReportBackPurchaseAccount(e.target.value)
                        }
                        size="small"
                        fullWidth
                        margin="dense"
                      />
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        marginBottom: "2rem",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            disabled={loading}
                            checked={fortnoxReportBackService}
                            onChange={(e) =>
                              setFortnoxReportBackService(e.target.checked)
                            }
                          />
                        }
                        label="Service"
                        sx={{ minWidth: "8rem" }}
                      />
                      <TextField
                        name="Servicekonto"
                        label="Servicekonto"
                        value={fortnoxReportBackServiceAccount}
                        onChange={(e) =>
                          setFortnoxReportBackServiceAccount(e.target.value)
                        }
                        size="small"
                        fullWidth
                        margin="dense"
                      />
                    </div>
                  </div>
                </div>

                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={loading}
                      checked={fortnoxSetAsSent}
                      onChange={(e) => setFortnoxSetAsSent(e.target.checked)}
                    />
                  }
                  label="Markera fakturan som skickad i Fortnox"
                />
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.5rem",
                  marginTop: "2rem",
                }}
              >
                <LoadingButton
                  startIcon={<SaveIcon />}
                  sx={{ width: "100%", gap: "0.5rem" }}
                  variant="contained"
                  onClick={() => updateLabelsAndReportBack()}
                  loading={loadingSave}
                >
                  Spara
                </LoadingButton>
                <LoadingButton
                  startIcon={<CachedIcon />}
                  variant="contained"
                  onClick={() => asyncFortnoxUpdateLabels()}
                  loading={loadingUpdate}
                  sx={{ width: "100%" }}
                >
                  Uppdatera Etiketter
                </LoadingButton>
                <Button
                  startIcon={<CloseIcon />}
                  sx={{ width: "100%" }}
                  variant="outlined"
                  color="error"
                  onClick={handleModalClose}
                >
                  Avbryt
                </Button>
              </div>
            </Box>
          </Modal>
        </div>
      )}
    </Paper>
  );
}
